
import type {
  SecureMessageAttachment,
  SecureMessageInbox,
  SecureMessageResponse,
  SecureMessageSent,
} from '@ld.consumer-portal/models';

import { MOCK_DATES } from './shared.mock';

function buildInboxMessage(
  id: string,
  subject: string,
  body: string,
  daysAgo: number,
  isRead: boolean,
  category = 'General Inquiry',
  attachments: Array<{ name: string; path: string }> = [],
): SecureMessageInbox {
  const mapped: SecureMessageAttachment[] = attachments.map((a) => ({
    FileName: a.name,
    FilePath: a.path,
  }));
  return {
    MessageId: id,
    CaseId: `CASE-${id}`,
    CorrelationId: `CORR-${id}`,
    Category: category,
    SubCategory: 'General',
    Subject: subject,
    Message: body,
    RequestDateTime: MOCK_DATES.paymentDate(daysAgo),
    SubmittedBy: 'loanDepot Support',
    Attachments: mapped,
    ReadFlag: isRead,
  };
}

function buildSentMessage(
  id: string,
  subject: string,
  body: string,
  daysAgo: number,
  category = 'General Inquiry',
  attachments: Array<{ name: string; path: string }> = [],
): SecureMessageSent {
  const mapped: SecureMessageAttachment[] = attachments.map((a) => ({
    FileName: a.name,
    FilePath: a.path,
  }));
  return {
    Id: id,
    MessageId: id,
    CaseId: `CASE-${id}`,
    CorrelationId: `CORR-${id}`,
    Category: category,
    SubCategory: 'General',
    Subject: subject,
    Message: body,
    RequestDateTime: MOCK_DATES.paymentDate(daysAgo),
    SubmittedBy: 'You',
    Attachments: mapped,
  };
}

function buildResponse(
  inbox: SecureMessageInbox[],
  sent: SecureMessageSent[],
  unreadCount: number,
): SecureMessageResponse {
  return {
    Inbox: {
      TotalMessages: inbox.length,
      TotalUnreadMessages: unreadCount,
      results: inbox,
    },
    Sent: {
      TotalMessages: sent.length,
      TotalUnreadMessages: 0,
      results: sent,
    },
  };
}

export const secureMessagesMock: Record<string, SecureMessageResponse> = {
  'with-messages': buildResponse(
    [
      buildInboxMessage(
        'msg-1',
        'Your payment has been received',
        'Thank you for your payment of $2,847.32. Your confirmation number is CONF-001.',
        2,
        false,
      ),
      buildInboxMessage(
        'msg-2',
        'Important: Your escrow analysis is ready',
        'Your annual escrow analysis has been completed. Please review the attached document for details about any changes to your monthly payment.',
        7,
        true,
        'Escrow',
        [{ name: 'Escrow_Analysis_2024.pdf', path: '/attachments/escrow.pdf' }],
      ),
      buildInboxMessage(
        'msg-3',
        'Welcome to loanDepot!',
        'Welcome to your new loanDepot account. We are here to help you manage your mortgage easily.',
        30,
        true,
      ),
    ],
    [
      buildSentMessage(
        'sent-1',
        'Question about my payment',
        'I have a question about my recent payment. Can you please help?',
        5,
      ),
    ],
    1,
  ),

  'no-messages': buildResponse([], [], 0),

  'unread-messages': buildResponse(
    [
      buildInboxMessage(
        'msg-1',
        'Action Required: Document Review',
        'Please review and sign the attached document at your earliest convenience.',
        1,
        false,
        'Documents',
        [{ name: 'Document_Review.pdf', path: '/attachments/review.pdf' }],
      ),
      buildInboxMessage(
        'msg-2',
        'Payment Reminder',
        'This is a friendly reminder that your payment is due in 5 days.',
        3,
        false,
      ),
      buildInboxMessage(
        'msg-3',
        'Rate Update Available',
        'Good news! You may be eligible for a rate reduction. Contact us to learn more.',
        5,
        false,
      ),
      buildInboxMessage(
        'msg-4',
        'Your payment has been posted',
        'Your payment has been successfully posted to your account.',
        10,
        true,
      ),
    ],
    [],
    3,
  ),

  'sent-only': buildResponse(
    [],
    [
      buildSentMessage('sent-1', 'Payoff quote request', 'I would like to request a payoff quote for my mortgage.', 2),
      buildSentMessage(
        'sent-2',
        'Address change request',
        'Please update my mailing address to 456 New Street, City, ST 12345.',
        10,
      ),
    ],
    0,
  ),
};

export const sendMessageMock = {
  success: {
    isSuccess: true,
    messageId: `msg-${Date.now()}`,
    message: 'Your message has been sent successfully.',
  },
  error: {
    isSuccess: false,
    messageId: '',
    message: 'Failed to send message. Please try again.',
  },
  'attachment-error': {
    isSuccess: false,
    messageId: '',
    message: 'Failed to upload attachment. File size may exceed limit.',
  },
};

export const markMessageReadMock = {
  success: {
    isSuccess: true,
  },
  error: {
    isSuccess: false,
    message: 'Failed to update message status.',
  },
};

export const deleteMessageMock = {
  success: {
    isSuccess: true,
  },
  error: {
    isSuccess: false,
    message: 'Failed to delete message.',
  },
};

export const unreadCountMock = {
  'with-unread': { count: 3 },
  'no-unread': { count: 0 },
  'single-unread': { count: 1 },
};
